/** @typedef {import('../types/models').Settlement} Settlement */
/** @typedef {import('../types/models').SettlementAccount} SettlementAccount */
/** @typedef {import('../types/api').PaginatedResponse} PaginatedResponse */
/** @typedef {import('../types/api').PaginationParams} PaginationParams */
/** @typedef {import('../types/api').ApiResponse} ApiResponse */

import { API_LIMIT, instance } from '../config/api';

/**
 * @param {PaginationParams} params
 * @returns {Promise<PaginatedResponse<Settlement>>}
 */
const fetchSettlements = async ({ offset, searchQuery, status, dateFilter }) => {
  const { from, to } = dateFilter;
  const { data } = await instance.get(
    `settlement?limit=${API_LIMIT}${offset ? '&offset=' + offset : ''}${
      searchQuery ? '&search_term=' + searchQuery : ''
    }${status ? '&status=' + status : ''}${from && to ? '&from=' + from + '&to=' + to : ''}`
  );
  return data;
};

/** @returns {Promise<ApiResponse<SettlementAccount[]>>} */
const fetchSettlementAccounts = async () => {
  const { data } = await instance.get('settlement/account');
  return data;
};

/**
 * @param {Partial<SettlementAccount>} account
 * @returns {Promise<ApiResponse<SettlementAccount>>}
 */
const addSettlementAccount = async (account) => {
  const { data } = await instance.post('settlement/account', account);
  return data;
};

/**
 * @param {string} account_number
 * @param {string} bank_code
 * @returns {Promise<ApiResponse<{ account_name: string, account_number: string }>>}
 */
const validateNgnAccount = async (account_number, bank_code) => {
  const { data } = await instance.post('settlement/account/validate', {
    type: 'FIAT',
    account_number,
    bank_code
  });
  return data;
};

/**
 * @param {Object} account
 * @returns {Promise<ApiResponse<Settlement>>}
 */
const requestSettlement = async (account) => {
  const { data } = await instance.post('settlement/request', account);
  return data;
};

/**
 * @param {Partial<SettlementAccount>} account
 * @returns {Promise<ApiResponse<SettlementAccount>>}
 */
const setAccountAsDefault = async (account) => {
  const { data } = await instance.patch('settlement/account', account);
  return data;
};

/**
 * @param {number} id
 * @returns {Promise<ApiResponse<Object>>}
 */
const deleteSettlementAccount = async (id) => {
  const { data } = await instance.delete(`settlement/account/${id}`);
  return data;
};

/**
 * @param {Object} payload
 * @returns {Promise<ApiResponse<Object>>}
 */
const processSettlement = async (payload) => {
  const { data } = await instance.post('settlement/process', payload);
  return data;
};

/**
 * @param {string} countryCode
 * @returns {Promise<{ label: string, name: string, value: string, code: string }[]>}
 */
// We are fetching only NG banks for now
const fetchListOfBanks = async (countryCode) => {
  const {
    data: { data: banks }
  } = await instance.get(`banks?country_code=${countryCode}`);

  return banks
    .map(({ name, code }) => {
      return { label: name, name, value: name, code };
    })
    .sort((a, b) => b.label - a.label);
};

export {
  addSettlementAccount,
  deleteSettlementAccount,
  fetchListOfBanks,
  fetchSettlementAccounts,
  fetchSettlements,
  processSettlement,
  requestSettlement,
  setAccountAsDefault,
  validateNgnAccount
};
